import React, { useCallback, useEffect, useRef, useState } from "react"
import { useToaster } from "@hellocontento/maillard"
import { useSelector, useDispatch } from "react-redux"
import { useHistory } from "react-router-dom"
import ReactLoading from "react-loading"
import { Text } from "rebass/styled-components"
import { Calendar, ChevronDown } from "styled-icons/feather"

import { callApi } from "utils/ContentoApi"
import { theme } from "theme"
import Button from "components/common/Button"
import Popper from "components/common/Popper"
import { MenuContainer } from "components/navigation/Menu"
import ConfirmModal from "./ConfirmModal"
import GenerationLoader from "./GenerationLoader"
import PostRow, { Post } from "./PostRow"
import ChannelAvatarBadge from "./ChannelAvatarBadge"
import RenameableName from "./RenameableName"

import * as styles from "../styles"
import { Health, PostBatchUpdate, Status } from "../types"
import { STATUS_FLOW } from "../values"
import { useSupportedChannels } from "../useSupportedChannels"
import { CfCategory, ChannelSelection, toggleCategorySelection, toggleChannelSelection } from "../categories"
import ChannelCategoryPicker from "./ChannelCategoryPicker"
import PlanningControl, { PLANNING_MENU_WIDTH, PLANNING_WEEK_MENU_ID } from "./PlanningControl"
import Tooltip from "./Tooltip"
import { describePlanningChange, formatPlanningPill, isSamePlanning } from "../planning"
import type { PlanningDefaults, PlanningSettings, PostingDay } from "../planning"
import { fetchPlanningDefaults } from "../planningApi"
import { formatCountdown, formatDateForDisplay, getBatchDisplayName, isGenerating } from "../utils"
import { openCfProfileReadyDialog, dismissCfProfileReadyDialog } from "../../../state/actions/ContentFactoryActions"
import { ContentFactoryState } from "../../../state/reducers/ContentFactoryReducer"

// The summary and its chevron sit to the right of the channel name, so the menu widens once any
// channel is selected and the categories can be reached.
const CHANNEL_MENU_WIDTH = 260
const CHANNEL_MENU_WIDTH_WITH_CATEGORIES = 380
const PLANNING_TOOLTIP_DELAY_MS = 400

export type PostBatch = {
  id: string
  name?: string // display name: the user-set label, else sourceName
  sourceName?: string
  channelSelections: ChannelSelection[]
  createdAt?: string
  draftIds: string[]
  status?: Status
  health?: Health
  updates?: PostBatchUpdate[]
  sourceType?: "url" | "file"
  estimatedCompletionAt?: number | null
  // Posts on this batch that haven't been surfaced to the user yet —
  // status ∈ {defined, planned, deprioritized_duplicate}. Picking from this
  // pool is what "generate more posts" does.
  reservoirCount: number
  // Whether this batch generates its own profile (drives the inline loader's
  // steps + the "go to profile" dialog). profileId is the profile it created.
  createsNewProfile: boolean
  profileId?: string | null
  postingDays: PostingDay[]
  firstMonday: string // YYYY-MM-DD
}

type Channel = {
  id: string
  username?: string
  service?: string
  serviceType?: string
  avatar?: string | null
}

type BatchBoxProps = {
  accountId: string
  batchId: string
  initialExpanded?: boolean
  onDeleted: () => void
  onGoToProfile?: (profileId: string) => void
}

interface AccountState {
  data: { channels?: Channel[]; timezone?: string }
}

interface RootState {
  account: AccountState
  contentFactory: ContentFactoryState
}

// DEV-ONLY: lets us preview the "Generate more posts" button before the
// backend returns `reservoirCount`. Visit any batch with ?previewReservoir=N
// to force the field. Remove once the backend lands the real field.
const getPreviewReservoirCount = (): number | null => {
  if (process.env.NODE_ENV === "production") return null
  const raw = new URLSearchParams(window.location.search).get("previewReservoir")
  if (raw === null) return null
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : null
}

const fetchBatch = async (accountId: string, batchId: string): Promise<PostBatch> => {
  const response = await callApi(
    { method: "get", url: `content-factory/accounts/${accountId}/postBatches/${batchId}` },
    true,
    true,
  )
  const previewReservoir = getPreviewReservoirCount()
  return {
    id: response.postBatchId,
    name: response.name ?? response.sourceName,
    sourceName: response.sourceName,
    channelSelections: response.channelSelections ?? [],
    createdAt: response.createdAt,
    draftIds: response.draftIds ?? [],
    status: response.status,
    health: response.health,
    updates: response.updates ?? [],
    sourceType: response.sourceType,
    estimatedCompletionAt: response.estimatedCompletionAt ?? null,
    reservoirCount: previewReservoir ?? response.reservoirCount ?? 0,
    createsNewProfile: response.createsNewProfile ?? false,
    profileId: response.profileId ?? null,
    // The frontend deploys ahead of the scheduler; until it ships, batches arrive without planning.
    postingDays: response.postingDays ?? [],
    firstMonday: response.firstMonday ?? "",
  }
}

const fetchPosts = async (accountId: string, batchId: string): Promise<Post[]> => {
  const response = await callApi(
    { method: "get", url: `content-factory/accounts/${accountId}/postBatches/${batchId}/posts` },
    true,
    true,
  )
  return response.posts ?? []
}

const hasReachedProfilePhaseDone = (batch: PostBatch): boolean => {
  const doneStatus: Status = batch.createsNewProfile ? "create_profile_done" : "create_source_done"
  return STATUS_FLOW.indexOf(batch.status ?? "pending") >= STATUS_FLOW.indexOf(doneStatus)
}

const BatchBox: React.FC<BatchBoxProps> = ({
  accountId,
  batchId,
  initialExpanded = false,
  onDeleted,
  onGoToProfile,
}) => {
  // Hooks
  const history = useHistory()
  const addToast = useToaster()
  const dispatch = useDispatch()
  const account = useSelector((state: RootState) => state.account.data)
  // Kept in the store so the "profile ready" dialog survives leaving and returning
  // to the content factory; opened on the in-session transition, cleared on dismiss.
  const showProfileReadyDialog = useSelector((state: RootState) => state.contentFactory?.profileReadyDialogBatchIds?.includes(batchId) ?? false)

  // Local (not persisted): expansion resets when the tab unmounts, so switching tabs collapses all rows.
  const [isExpanded, setIsExpanded] = useState(initialExpanded)

  // State
  const [batch, setBatch] = useState<PostBatch | null>(null)
  const [posts, setPosts] = useState<Post[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [channelSelections, setChannelSelections] = useState<ChannelSelection[]>([])
  const [showChannelMenu, setShowChannelMenu] = useState(false)
  const [expandedCategoryChannelId, setExpandedCategoryChannelId] = useState<string | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isGenerateMoreSubmitting, setIsGenerateMoreSubmitting] = useState(false)
  const [showPlanningMenu, setShowPlanningMenu] = useState(false)
  const [planningDraft, setPlanningDraft] = useState<PlanningSettings | null>(null)
  const [planningDefaults, setPlanningDefaults] = useState<PlanningDefaults | null>(null)
  const [isSavingPlanning, setIsSavingPlanning] = useState(false)
  const [showPlanningBlockedModal, setShowPlanningBlockedModal] = useState(false)

  // Refs
  const channelButtonRef = useRef<HTMLButtonElement>(null)
  const planningButtonRef = useRef<HTMLButtonElement>(null)
  // True while the profile isn't ready yet; cleared once the dialog opens — so a reload of a finished batch never opens it.
  const profileNotReadyRef = useRef(false)

  // Derived values
  const channels = useSupportedChannels()
  const timezone = account.timezone ?? "Europe/Brussels"
  const displayName = getBatchDisplayName(batch?.name)
  const hasCustomName = !!batch?.name && batch.name !== batch.sourceName
  const selectedChannelIds = channelSelections.map((selection) => selection.channelId)
  const selectedChannels = selectedChannelIds
    .map((channelId) => channels.find((channel) => channel.id === channelId))
    .filter((channel): channel is Channel => !!channel)
  const leadChannel = selectedChannels[0] ?? null

  // Derived
  const hasChannels = channels.length > 0
  const countdown = formatCountdown(batch?.estimatedCompletionAt)
  const isFailed = batch?.health === "failed"
  // Profile-phase failures render in the loader; posts-phase failures get their own
  // inline block below. The loader also stays up alongside the profile-ready dialog.
  const showLoader =
    isExpanded &&
    !!batch &&
    ((!hasReachedProfilePhaseDone(batch) && (isGenerating(batch) || isFailed)) || showProfileReadyDialog)
  const showPostsPhaseFailure = !!batch && isFailed && hasReachedProfilePhaseDone(batch)
  // The failure handler writes a user-facing reason as the batch's final update
  const lastUpdateMessage = batch?.updates?.length ? batch.updates[batch.updates.length - 1]?.message : undefined

  // Callbacks
  const handleDismissFailure = useCallback(async () => {
    try {
      await callApi(
        { method: "put", url: `content-factory/accounts/${accountId}/postBatches/${batchId}/status`, data: { status: "user_acknowledged_failure" } },
        true,
        true,
      )
    } catch (err) {
      console.error("Failed to acknowledge batch failure", { accountId, batchId, err })
    }
    onDeleted()
  }, [accountId, batchId, onDeleted])

  const handleGoToProfile = useCallback(() => {
    dispatch(dismissCfProfileReadyDialog(batchId))
    if (batch?.profileId) onGoToProfile?.(batch.profileId)
  }, [batch, batchId, onGoToProfile, dispatch])

  const headerChannelLabel = selectedChannels.length > 1
    ? `${selectedChannels.length} channels`
    : selectedChannels.length === 1
      ? leadChannel?.username
      : hasChannels ? "Select channels" : "Connect a channel"

  const [showChannelBlockedModal, setShowChannelBlockedModal] = useState(false)

  const toggleChannelMenu = useCallback((event: React.MouseEvent) => {
    event.stopPropagation()
    if (batch && isGenerating(batch)) {
      setShowChannelBlockedModal(true)
      return
    }
    if (hasChannels) {
      setShowChannelMenu((prev) => !prev)
    } else {
      history.push(`/accounts/${accountId}/settings/channels`)
    }
  }, [accountId, batch, hasChannels, history])

  const closeChannelMenu = useCallback(() => {
    setShowChannelMenu(false)
  }, [])

  const hasPlanning = !!batch && batch.postingDays.length > 0 && !!batch.firstMonday
  const planningLabel = batch && hasPlanning ? formatPlanningPill(batch) : "Planning"
  const planningChanged = !!batch && !!planningDraft && !isSamePlanning(planningDraft, batch)
  const movablePostCount = posts.filter((post) => post.displayStatus === "draft").length
  const hasSeriesPost = posts.some((post) => !!post.series)

  const togglePlanningMenu = useCallback(async (event: React.MouseEvent) => {
    event.stopPropagation()
    if (!batch) return
    if (isGenerating(batch)) {
      setShowPlanningBlockedModal(true)
      return
    }
    if (showPlanningMenu) {
      setShowPlanningMenu(false)
      return
    }
    setPlanningDraft({ postingDays: batch.postingDays, firstMonday: batch.firstMonday })
    setShowPlanningMenu(true)
    try {
      setPlanningDefaults(await fetchPlanningDefaults(accountId))
    } catch (err) {
      console.error("Failed to fetch planning defaults", { accountId, batchId, err })
      addToast("Failed to load the planning options.", "error")
      setShowPlanningMenu(false)
    }
  }, [accountId, addToast, batch, batchId, showPlanningMenu])

  const closePlanningMenu = useCallback(() => {
    setShowPlanningMenu(false)
  }, [])

  const savePlanning = useCallback(async () => {
    if (!planningDraft) return
    setIsSavingPlanning(true)
    try {
      await callApi(
        { method: "put", url: `content-factory/accounts/${accountId}/postBatches/${batchId}/planning`, data: planningDraft },
        true,
        true,
      )
      setShowPlanningMenu(false)
      // The batch now reports the planning status; the polling effect streams the re-dated posts in.
      setBatch(await fetchBatch(accountId, batchId))
    } catch (err) {
      console.error("Failed to update post batch planning", { accountId, batchId, err })
      addToast("Failed to update the planning.", "error")
    } finally {
      setIsSavingPlanning(false)
    }
  }, [accountId, addToast, batchId, planningDraft])

  const [showRemoveAllChannelsModal, setShowRemoveAllChannelsModal] = useState(false)
  const [isUpdatingChannels, setIsUpdatingChannels] = useState(false)

  const putChannelSelection = useCallback(
    async (newChannelSelections: ChannelSelection[]) => {
      setIsUpdatingChannels(true)
      try {
        const url = `content-factory/accounts/${accountId}/postBatches/${batchId}/channel`
        await callApi({ method: "put", url, data: { channelSelections: newChannelSelections } }, true, true)
        // Reload rather than trust newChannelIds: the server ranks the selection, and the first
        // channel of that ranking is the one the header and the post previews identify with.
        const [updatedBatch, freshPosts] = await Promise.all([fetchBatch(accountId, batchId), fetchPosts(accountId, batchId)])
        setBatch(updatedBatch)
        setChannelSelections(updatedBatch.channelSelections)
        setPosts(freshPosts)
      } catch (err) {
        console.error("Failed to update post batch channels", { accountId, batchId, err })
        addToast("Failed to update post batch channels.", "error")
      } finally {
        setIsUpdatingChannels(false)
      }
    },
    [accountId, addToast, batchId],
  )

  const handleChannelToggle = useCallback(
    async (channelId: string) => {
      const isSelected = channelSelections.some((selection) => selection.channelId === channelId)
      if (isSelected && channelSelections.length === 1) {
        setShowRemoveAllChannelsModal(true) // emptying the selection deletes the linked drafts server-side
        return
      }
      if (isSelected && expandedCategoryChannelId === channelId) {
        setExpandedCategoryChannelId(null) // else re-selecting it later springs the drawer open
      }
      await putChannelSelection(toggleChannelSelection(channelSelections, channelId))
    },
    [putChannelSelection, channelSelections, expandedCategoryChannelId],
  )

  const handleCategoryToggle = useCallback(
    async (channelId: string, category: CfCategory) => {
      const newChannelSelections = toggleCategorySelection(channelSelections, channelId, category)
      // Turning the last category off means the channel publishes nothing, which is deselecting it.
      if (newChannelSelections.length === 0) {
        setShowRemoveAllChannelsModal(true) // emptying the selection deletes the linked drafts server-side
        return
      }
      await putChannelSelection(newChannelSelections)
    },
    [putChannelSelection, channelSelections],
  )

  const handleGenerateMore = useCallback(async () => {
    if (!batch || isGenerateMoreSubmitting) return
    const count = Math.min(10, batch.reservoirCount)
    if (count <= 0) return
    setIsGenerateMoreSubmitting(true)
    try {
      await callApi(
        { method: "post", url: `content-factory/accounts/${accountId}/postBatches/${batchId}/posts`, data: { count } },
        true,
        true,
      )
      // Refetch the batch so its status flips to "generating"; the existing
      // polling effect picks it up from there and streams in new posts.
      const updatedBatch = await fetchBatch(accountId, batchId)
      setBatch(updatedBatch)
    } catch (err) {
      console.error("Failed to generate more posts", { accountId, batchId, err })
      addToast("Failed to generate more posts.", "error")
    } finally {
      setIsGenerateMoreSubmitting(false)
    }
  }, [accountId, addToast, batch, batchId, isGenerateMoreSubmitting])

  const handleDelete = useCallback(async () => {
    try {
      await callApi({ method: "delete", url: `content-factory/accounts/${accountId}/postBatches/${batchId}` }, true, true)
      onDeleted()
    } catch (err) {
      console.error("Failed to delete post batch", { accountId, batchId, err })
      addToast("Failed to delete post batch.", "error")
    }
  }, [accountId, addToast, batchId, onDeleted])

  // Callbacks for PostRow - simple state updates only
  const handlePostDeleted = useCallback((postId: string) => {
    setPosts((prev) => prev.filter((p) => p.id !== postId))
  }, [])

  const handlePostUpdated = useCallback((updatedPost: Post) => {
    setPosts((prev) => prev.map((p) => (p.id === updatedPost.id ? updatedPost : p)))
  }, [])

  const handleRename = async (nextName: string) => {
    const previousName = batch?.name
    if (nextName) setBatch((prev) => prev && { ...prev, name: nextName })
    try {
      const response = await callApi(
        { method: "put", url: `content-factory/accounts/${accountId}/postBatches/${batchId}/name`, data: { name: nextName } },
        true,
        true,
      )
      setBatch((prev) => prev && { ...prev, name: response.name })
    } catch (err) {
      console.error("Failed to rename batch", err)
      setBatch((prev) => prev && { ...prev, name: previousName })
      addToast("Failed to rename batch", "error")
    }
  }

  const toggleExpanded = useCallback(() => {
    const next = !isExpanded
    setIsExpanded(next)
    // Collapsing shortens the page and the browser yanks the scroll position to
    // fit; return to the top of the result page so the user lands somewhere sane.
    if (!next) {
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }, [isExpanded])

  // Effects
  useEffect(() => {
    fetchBatch(accountId, batchId)
      .then((batch) => {
        setBatch(batch)
        setChannelSelections(batch.channelSelections)
      })
      .catch((err) => {
        console.error("Failed to fetch post batch", { accountId, batchId, err })
        addToast("Failed to load post batch.", "error")
      })
      .finally(() => setIsLoading(false))
  }, [accountId, batchId, addToast])

  // Fetch posts initially
  useEffect(() => {
    if (!batch) return

    const loadPosts = async () => {
      try {
        const fetchedPosts = await fetchPosts(accountId, batchId)
        setPosts(fetchedPosts)
      } catch (err) {
        console.error("Failed to fetch posts", { accountId, batchId, err })
      }
    }

    loadPosts()
  }, [accountId, batchId, batch])

  useEffect(() => {
    if (!batch?.createsNewProfile) return
    if (!hasReachedProfilePhaseDone(batch)) {
      profileNotReadyRef.current = true
    } else if (profileNotReadyRef.current && batch.health === "healthy") {
      profileNotReadyRef.current = false
      dispatch(openCfProfileReadyDialog(batchId))
    }
  }, [batch, batchId, dispatch])

  // Poll (while expanded) when the batch is generating OR any post's visual is regenerating, so
  // new posts and regenerated visuals stream in. Polling stops once neither is true.
  const hasRegeneratingPost = posts.some((post) => post.regeneratingVisual)
  useEffect(() => {
    if (!batch || !isExpanded) return
    if (!isGenerating(batch) && !hasRegeneratingPost) return

    const intervalId = setInterval(async () => {
      try {
        const updatedBatch = await fetchBatch(accountId, batchId)
        setBatch(updatedBatch)

        if (updatedBatch.health !== "healthy") return

        const fetchedPosts = await fetchPosts(accountId, batchId)
        setPosts(fetchedPosts)
      } catch (err) {
        console.error("Failed to poll batch updates", { accountId, batchId, err })
      }
    }, 1000)

    return () => clearInterval(intervalId)
  }, [accountId, batchId, batch, isExpanded, hasRegeneratingPost])

  if (isLoading) {
    return (
      <styles.CardContainer>
        <styles.CardHeader style={{ justifyContent: "center", padding: "20px" }}>
          <Text fontSize={14} color="text02">Loading...</Text>
        </styles.CardHeader>
      </styles.CardContainer>
    )
  }

  if (!batch) {
    return null
  }

  return (
    <>
      <styles.CardContainer data-testid={`post-batch-card-${batchId}`}>
        <styles.CardHeader onClick={toggleExpanded} data-expanded={isExpanded} data-testid={`post-batch-header-${batchId}`}>
          <styles.ChannelSelectButton
            ref={channelButtonRef}
            type="button"
            onClick={toggleChannelMenu}
            data-testid={`channel-selector-${batchId}`}
          >
            <styles.ChannelAvatarGroup>
              {selectedChannels.length === 0 ? (
                <ChannelAvatarBadge channel={null} testId="channel-avatar-empty" />
              ) : (
                selectedChannels.slice(0, 3).map((selected) => (
                  <ChannelAvatarBadge
                    key={selected.id}
                    channel={selected}
                    testId={`channel-avatar-${selected.id}`}
                    serviceTestId={`channel-service-${selected.id}`}
                  />
                ))
              )}
            </styles.ChannelAvatarGroup>
            <styles.ChannelCountLabel>{headerChannelLabel}</styles.ChannelCountLabel>
            <ChevronDown size={15} color="currentColor" />
          </styles.ChannelSelectButton>

          <Tooltip content={planningLabel} interactive={false} showDelayMs={PLANNING_TOOLTIP_DELAY_MS}>
            <styles.PlanningIconButton
              ref={planningButtonRef}
              type="button"
              $locked={isGenerating(batch)}
              aria-label={planningLabel}
              onClick={togglePlanningMenu}
              data-testid={`planning-selector-${batchId}`}
            >
              <Calendar size={16} />
            </styles.PlanningIconButton>
          </Tooltip>

          <styles.BatchMeta data-testid={`batch-meta-${batchId}`}>
            <RenameableName
              name={displayName}
              label="batch"
              testId={`rename-batch-${batchId}`}
              onRename={handleRename}
              suffix={
                <>
                  {hasCustomName && ` · from ${getBatchDisplayName(batch.sourceName)}`}
                  {` · ${formatDateForDisplay(batch.createdAt, timezone)}`}
                </>
              }
            >
              {!hasCustomName && "Generated from "}
              <styles.BatchSourceName>{displayName}</styles.BatchSourceName>
            </RenameableName>
          </styles.BatchMeta>

          <styles.BatchHeaderRight>
            <styles.CountChip data-testid={`post-count-badge-${batchId}`}>
              {posts.length} / {posts.length + batch.reservoirCount} ready
            </styles.CountChip>
            <styles.HeaderIconButton
              type="button"
              $danger
              aria-label="Delete batch"
              data-testid={`delete-batch-${batchId}`}
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation()
                setShowDeleteModal(true)
              }}
            >
              <i className="icon-delete" style={{ fontSize: 18 }} />
            </styles.HeaderIconButton>
            <styles.HeaderIconButton type="button" aria-label={isExpanded ? "Collapse" : "Expand"}>
              <styles.ExpandIcon isExpanded={isExpanded}>
                <ChevronDown size={18} />
              </styles.ExpandIcon>
            </styles.HeaderIconButton>
          </styles.BatchHeaderRight>
        </styles.CardHeader>
        <Popper
          referenceElement={channelButtonRef.current}
          visible={showChannelMenu}
          placement="bottom-start"
          onClose={closeChannelMenu}
          exceptions={channelButtonRef.current ? [channelButtonRef.current, "remove-all-channels-modal"] : ["remove-all-channels-modal"]}
        >
          <MenuContainer width={channelSelections.length > 0 ? CHANNEL_MENU_WIDTH_WITH_CATEGORIES : CHANNEL_MENU_WIDTH}>
            <div style={{ padding: "6px 0" }}>
              {channels.map((channel) => {
                const selection = channelSelections.find(({ channelId }) => channelId === channel.id)
                const isSelected = selection !== undefined
                const isCategoryDrawerOpen = isSelected && expandedCategoryChannelId === channel.id
                return (
                  <ChannelCategoryPicker
                    key={channel.id}
                    variant="menu"
                    selection={selection}
                    isOpen={isCategoryDrawerOpen}
                    disabled={isUpdatingChannels}
                    testIdPrefix={`${batchId}-${channel.id}`}
                    onToggleOpen={() => setExpandedCategoryChannelId(isCategoryDrawerOpen ? null : channel.id)}
                    onToggleCategory={(category) => handleCategoryToggle(channel.id, category)}
                  >
                    <styles.ChannelMenuItem
                      type="button"
                      onClick={(event: React.MouseEvent) => {
                        event.stopPropagation()
                        handleChannelToggle(channel.id)
                      }}
                      disabled={isUpdatingChannels}
                      data-selected={isSelected}
                      data-testid={`channel-option-${batchId}-${channel.id}`}
                    >
                      <styles.ChannelMenuCheckbox
                        type="checkbox"
                        checked={isSelected}
                        disabled={isUpdatingChannels}
                        readOnly
                        tabIndex={-1}
                        data-testid={`channel-option-checkbox-${batchId}-${channel.id}`}
                      />
                      <ChannelAvatarBadge
                        channel={channel}
                        testId={`channel-option-avatar-${batchId}-${channel.id}`}
                        serviceTestId={`channel-option-service-${batchId}-${channel.id}`}
                      />
                      <styles.ChannelMenuName>{channel.username}</styles.ChannelMenuName>
                    </styles.ChannelMenuItem>
                  </ChannelCategoryPicker>
                )
              })}
            </div>
          </MenuContainer>
        </Popper>

        <Popper
          referenceElement={planningButtonRef.current}
          visible={showPlanningMenu}
          placement="bottom-start"
          onClose={closePlanningMenu}
          exceptions={planningButtonRef.current ? [planningButtonRef.current, PLANNING_WEEK_MENU_ID] : [PLANNING_WEEK_MENU_ID]}
        >
          <MenuContainer width={PLANNING_MENU_WIDTH}>
            <styles.PlanningPopoverBody>
              {planningDraft && planningDefaults ? (
                <>
                  <PlanningControl
                    value={planningDraft}
                    defaults={planningDefaults}
                    onChange={setPlanningDraft}
                    testIdPrefix={`planning-${batchId}`}
                    disabled={isSavingPlanning}
                  />
                  {planningChanged && (
                    <styles.PlanningPopoverNote data-testid={`planning-note-${batchId}`}>
                      {describePlanningChange({ movablePostCount, postingDays: planningDraft.postingDays })}
                    </styles.PlanningPopoverNote>
                  )}
                  <styles.PlanningActions>
                    <Button variant="secondary" size="sm" onClick={closePlanningMenu} disabled={isSavingPlanning}>
                      Cancel
                    </Button>
                    <Button size="sm" onClick={savePlanning} disabled={!planningChanged || isSavingPlanning} data-testid={`planning-save-${batchId}`}>
                      {isSavingPlanning ? "Saving..." : "Save and move posts"}
                    </Button>
                  </styles.PlanningActions>
                </>
              ) : (
                <styles.PlanningPopoverNote>Loading planning options...</styles.PlanningPopoverNote>
              )}
            </styles.PlanningPopoverBody>
          </MenuContainer>
        </Popper>

        <styles.CardBody isOpen={isExpanded}>
          <styles.CardContent>
            <styles.CardInner style={{ gap: "12px" }} data-testid={`post-batch-body-${batchId}`}>
              {showLoader && (
                <GenerationLoader
                  status={batch.status}
                  health={batch.health}
                  updates={batch.updates}
                  estimatedCompletionAt={batch.estimatedCompletionAt}
                  sourceType={batch.sourceType}
                  showProfileStep={batch.createsNewProfile}
                  onDismiss={handleDismissFailure}
                />
              )}

              {showProfileReadyDialog && (
                <styles.ProfileReadyDialog data-testid={`profile-ready-dialog-${batchId}`}>
                  <styles.InlineTitle>Your new profile is ready</styles.InlineTitle>
                  <styles.InlineSubtitle>Want to have a look?</styles.InlineSubtitle>
                  <styles.ProfileReadyActions>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => dispatch(dismissCfProfileReadyDialog(batchId))}
                      data-testid={`profile-ready-stay-${batchId}`}
                    >
                      Stay here
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleGoToProfile}
                      data-testid={`profile-ready-go-${batchId}`}
                    >
                      Go to profile
                    </Button>
                  </styles.ProfileReadyActions>
                </styles.ProfileReadyDialog>
              )}

              {showPostsPhaseFailure && (
                <styles.GeneratingPlaceholder data-testid={`post-batch-failure-${batchId}`}>
                  <styles.InlineTitle>Post generation failed</styles.InlineTitle>
                  <styles.InlineSubtitle>
                    {lastUpdateMessage ?? "Something went wrong while generating your posts. Please try again."}
                  </styles.InlineSubtitle>
                  <Button onClick={handleDismissFailure} variant="primary" size="sm" style={{ marginTop: 12 }}>
                    Dismiss
                  </Button>
                </styles.GeneratingPlaceholder>
              )}

              {/* Show posts if available */}
              {posts.length > 0 && (
                <div data-testid={`posts-list-${batchId}`}>
                  {posts.map((post) => (
                    <PostRow
                      key={post.id}
                      accountId={accountId}
                      profileId={batch?.profileId}
                      post={post}
                      timezone={timezone}
                      leadChannel={leadChannel}
                      accountChannels={channels}
                      reserveSeriesSlot={hasSeriesPost}
                      onDeleted={handlePostDeleted}
                      onUpdated={handlePostUpdated}
                    />
                  ))}
                </div>
              )}

              {/* Show generating spinner while batch is in progress (but not during
                  the profile phase, where the loader above is the progress indicator) */}
              {isGenerating(batch) && !showLoader && (
                <styles.GeneratingPlaceholder data-testid={`generating-spinner-${batchId}`}>
                  <styles.GeneratingSpinnerRow>
                    <ReactLoading type="spin" color={theme.colors.grey02} width={20} height={20} />
                    <styles.GeneratingLabel>Generating more posts...</styles.GeneratingLabel>
                  </styles.GeneratingSpinnerRow>
                  {countdown && posts.length === 0 && (
                    <styles.GeneratingCountdown>Approx. {countdown} remaining</styles.GeneratingCountdown>
                  )}
                </styles.GeneratingPlaceholder>
              )}

              {/* Show empty state only when done and no posts */}
              {!isGenerating(batch) && !isFailed && posts.length === 0 && (
                <styles.InlineSubtitle>No posts available.</styles.InlineSubtitle>
              )}

              {!isGenerating(batch) && !isFailed && batch.reservoirCount > 0 && (
                <styles.GenerateMoreSlot>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={handleGenerateMore}
                    disabled={isGenerateMoreSubmitting}
                    data-testid={`generate-more-${batchId}`}
                  >
                    Generate {Math.min(10, batch.reservoirCount)} more posts
                  </Button>
                </styles.GenerateMoreSlot>
              )}
            </styles.CardInner>
          </styles.CardContent>
        </styles.CardBody>
      </styles.CardContainer>

      <ConfirmModal
        modalName="delete-post-batch-modal"
        title="Delete this batch?"
        description={`You are about to delete posts generated from ${displayName}. This can't be undone.`}
        icon={<span className="icon-delete" />}
        confirmLabel="Delete batch"
        confirmVariant="danger"
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={async () => {
          await handleDelete()
          setShowDeleteModal(false)
        }}
      />

      <ConfirmModal
        modalName="channel-blocked-modal"
        title="Channel can't be changed yet"
        description="Posts are still being generated. You can change the channel once generation is complete."
        isOpen={showChannelBlockedModal}
        onClose={() => setShowChannelBlockedModal(false)}
      />

      <ConfirmModal
        modalName="planning-blocked-modal"
        title="Planning can't be changed yet"
        description="Posts are still being generated. You can change the posting days and the starting week once generation is complete."
        isOpen={showPlanningBlockedModal}
        onClose={() => setShowPlanningBlockedModal(false)}
      />

      <ConfirmModal
        modalName="remove-all-channels-modal"
        title="Remove all channels?"
        description="The drafts of this batch will be deleted, including any changes you made to them in the composer. Posts that are already scheduled or published stay untouched."
        confirmLabel="Remove channels"
        confirmVariant="danger"
        isOpen={showRemoveAllChannelsModal}
        onClose={() => setShowRemoveAllChannelsModal(false)}
        onConfirm={async () => {
          setShowRemoveAllChannelsModal(false)
          await putChannelSelection([])
        }}
      />
    </>
  )
}

export default BatchBox
